import api from '@/api/base';
import type { NotificationModel } from '@/api/types';

// API URLs for notifications
const URLS = {
  notifications: '/notify',
  notificationById: (id: string) => `/notify/${id}`,
  markAsRead: (id: string) => `/notify/${id}/read`,
  markAllAsRead: '/notify/read-all',
  disableAll: '/notify',
} as const;

// NOTE: unlike most endpoints, the backend Notify controller returns BARE
// payloads (res.json(notifications) / res.json(notify)) — it does NOT use the
// shared { status, results, data } envelope. So responses are typed as the raw
// model, not ApiResponse<Model>. Action results (mark-all / disable) return an
// opaque summary, typed unknown.
type NotificationPayload = Record<string, unknown>;

// Notifications API methods
const notificationsApi = {
  // Get all (unread) notifications — bare array
  getNotifications: (token?: string) => {
    return api.get<NotificationModel[]>(URLS.notifications, {
      token,
    });
  },

  // Get notification by ID — bare object
  getNotificationById: (id: string, token?: string) => {
    return api.get<NotificationModel>(URLS.notificationById(id), {
      token,
    });
  },

  // Create new notification — bare object
  createNotification: (
    notificationData: NotificationPayload,
    token?: string,
  ) => {
    return api.post<NotificationModel>(URLS.notifications, notificationData, {
      token,
    });
  },

  // Mark notification as read — bare object
  markAsRead: (id: string, token?: string) => {
    return api.patch<NotificationModel>(URLS.markAsRead(id), {}, { token });
  },

  // Mark all notifications as read — opaque result summary
  markAllAsRead: (token?: string) => {
    return api.patch<unknown>(URLS.markAllAsRead, {}, { token });
  },

  // Disable all notifications (clear all) — opaque result summary
  disableAllNotifications: (token?: string) => {
    return api.patch<unknown>(URLS.disableAll, {}, { token });
  },

  // Delete notification
  deleteNotification: (id: string, token?: string) => {
    return api.delete<unknown>(URLS.notificationById(id), { token });
  },
};

export default notificationsApi;
